import * as React from 'react';

import { SlDropdown, SlMenu } from '@shoelace-style/shoelace/dist/react';
import { TeamRole } from '@showbie-socrative/socrative-utils';
import { translate } from '~l10n/client';
import { useAtomValue } from 'jotai';

import { toast } from '~shared/components/Toast';
import { selectedTeamIdAtom } from '~teacher/components/Account/atoms';
import {
  useHandleChangeToAdmin,
  useHandleChangeToMember,
} from '~teacher/components/Account/TeamMembers/TeamMembersTable/RoleActionMenu/hooks';
import { RoleActionMenuItem } from '~teacher/components/Account/TeamMembers/TeamMembersTable/RoleActionMenu/RoleActionMenuItem';
import { RoleMenuTriggerButton } from '~teacher/components/Account/TeamMembers/TeamMembersTable/RoleActionMenu/RoleMenuTriggerButton';
import { roleNamesAndDescriptions } from '~teacher/components/Account/TeamMembers/TeamMembersTable/RoleActionMenu/roleNamesAndDescriptions';

interface RoleActionMenuAdminProps {
  targetPublicId: string;
  targetMemberRole: TeamRole;
}

/**
 * Component for rendering the role action menu for an admin.
 *
 */
export function RoleActionMenuAdmin({
  targetPublicId,
  targetMemberRole,
}: RoleActionMenuAdminProps) {
  // currently selected team
  const teamId = useAtomValue(selectedTeamIdAtom);
  // Hook to handle the change of a team member's role to Member
  const { handleChangeToMember } = useHandleChangeToMember({
    onError: () => toast.critical(translate('An error has occurred')),
    onSuccess: () => toast(translate('Done')),
    teamId,
    targetPublicId,
    targetMemberRole,
  });

  // Hook to handle the change of a team member's role to Admin
  const { handleChangeToAdmin } = useHandleChangeToAdmin({
    onError: () => toast.critical(translate('An error has occurred')),
    onSuccess: () => toast(translate('Done')),
    teamId: teamId,
    targetPublicId: targetPublicId,
    targetTeamRole: targetMemberRole,
  });

  return (
    <SlDropdown>
      <RoleMenuTriggerButton>
        {roleNamesAndDescriptions[targetMemberRole].title}
      </RoleMenuTriggerButton>
      <SlMenu className="action-menu">
        <RoleActionMenuItem
          key={`${TeamRole.Member}-to-${targetMemberRole}`}
          menuItemTeamRole={TeamRole.Member}
          targetMemberRole={targetMemberRole}
          onClick={handleChangeToMember}
        />
        <RoleActionMenuItem
          key={`${TeamRole.Admin}}-to-${targetMemberRole}`}
          menuItemTeamRole={TeamRole.Admin}
          targetMemberRole={targetMemberRole}
          onClick={handleChangeToAdmin}
        />
      </SlMenu>
    </SlDropdown>
  );
}
